import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, XCircle, AlertTriangle, RefreshCw, Shield, Clock, Activity, ArrowLeft } from 'lucide-react';

const API = '';

export default function HealthCheck() {
  const navigate = useNavigate();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    fetchLatest();
    fetchHistory();
  }, []);

  const fetchLatest = async () => {
    try {
      const res = await fetch(`${API}/api/health-check/latest`);
      const data = await res.json();
      setResults(data);
    } catch {
      setResults(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await fetch(`${API}/api/health-check/history`);
      const data = await res.json();
      setHistory(data);
    } catch {
      setHistory([]);
    }
  };

  const runCheck = async () => {
    setRunning(true);
    try {
      const res = await fetch(`${API}/api/health-check`);
      const data = await res.json();
      setResults(data);
      fetchHistory();
    } catch {
      setResults({ overall: 'error', detail: 'Could not reach the server' });
    } finally {
      setRunning(false);
    }
  };

  const StatusIcon = ({ status }) => {
    if (status === 'pass') return <CheckCircle2 className="h-5 w-5 text-green-500" />;
    if (status === 'fail') return <XCircle className="h-5 w-5 text-red-500" />;
    if (status === 'warn') return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
    return <AlertTriangle className="h-5 w-5 text-gray-400" />;
  };

  const OverallBadge = ({ overall }) => {
    const styles = {
      healthy: 'bg-green-100 text-green-800 border-green-200',
      unhealthy: 'bg-red-100 text-red-800 border-red-200',
      degraded: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      unknown: 'bg-gray-100 text-gray-600 border-gray-200',
      error: 'bg-red-100 text-red-800 border-red-200',
    };
    return (
      <span className={`inline-flex items-center px-4 py-1.5 rounded-full text-sm font-semibold border ${styles[overall] || styles.unknown}`} data-testid="health-overall-status">
        {overall === 'healthy' && <CheckCircle2 className="h-4 w-4 mr-1.5" />}
        {overall === 'unhealthy' && <XCircle className="h-4 w-4 mr-1.5" />}
        {overall === 'degraded' && <AlertTriangle className="h-4 w-4 mr-1.5" />}
        {(overall || 'unknown').toUpperCase()}
      </span>
    );
  };

  const formatLabel = (key) => {
    return key
      .replace('collection_', '')
      .replace('route_', '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  const groupChecks = (checks) => {
    if (!checks) return {};
    const groups = { 'Core Systems': {}, 'Data Collections': {}, 'API Routes': {}, 'Other': {} };
    Object.entries(checks).forEach(([key, val]) => {
      if (key === 'database' || key === 'cloud_storage' || key === 'auth_system') {
        groups['Core Systems'][key] = val;
      } else if (key.startsWith('collection_')) {
        groups['Data Collections'][key] = val;
      } else if (key.startsWith('route_')) {
        groups['API Routes'][key] = val;
      } else {
        groups['Other'][key] = val;
      }
    });
    return groups;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const grouped = results?.checks ? groupChecks(results.checks) : {};

  return (
    <div className="min-h-screen bg-gray-50" data-testid="health-check-page">
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-1.5 px-3 py-2 text-gray-600 hover:text-blue-600 hover:bg-gray-100 rounded-lg transition"
              data-testid="health-back-btn"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="text-sm font-medium">Back</span>
            </button>
            <Shield className="h-8 w-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">System Health Check</h1>
              <p className="text-sm text-gray-500">Legacy Forge Family — Post-Deployment Verification</p>
            </div>
          </div>
          <button
            onClick={runCheck}
            disabled={running}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
            data-testid="run-health-check-btn"
          >
            <RefreshCw className={`h-4 w-4 ${running ? 'animate-spin' : ''}`} />
            {running ? 'Running...' : 'Run Check'}
          </button>
        </div>

        {/* Overall Status */}
        {results && (
          <div className="bg-white rounded-xl shadow-sm border p-6 mb-6" data-testid="health-results">
            <div className="flex items-center justify-between mb-4">
              <OverallBadge overall={results.overall} />
              <div className="text-right text-sm text-gray-500">
                {results.triggered_by === 'auto_startup' && (
                  <span className="inline-flex items-center gap-1 text-blue-600 font-medium">
                    <Activity className="h-3.5 w-3.5" /> Auto-run after deploy
                  </span>
                )}
                {results.triggered_by === 'manual' && (
                  <span className="text-gray-600">Manual check</span>
                )}
              </div>
            </div>

            {results.timestamp && (
              <p className="text-xs text-gray-400 mb-4 flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                {new Date(results.timestamp).toLocaleString()}
              </p>
            )}

            {/* Summary bar */}
            {results.total_checks && (
              <div className="flex items-center gap-4 text-sm mb-6">
                <span className="flex items-center gap-1 text-green-600 font-medium">
                  <CheckCircle2 className="h-4 w-4" /> {results.passed} passed
                </span>
                {results.failed > 0 && (
                  <span className="flex items-center gap-1 text-red-600 font-medium">
                    <XCircle className="h-4 w-4" /> {results.failed} failed
                  </span>
                )}
                {results.warnings > 0 && (
                  <span className="flex items-center gap-1 text-yellow-600 font-medium">
                    <AlertTriangle className="h-4 w-4" /> {results.warnings} warnings
                  </span>
                )}
                <span className="text-gray-400">of {results.total_checks} checks</span>
              </div>
            )}

            {/* Grouped checks */}
            {Object.entries(grouped).map(([groupName, checks]) => {
              if (Object.keys(checks).length === 0) return null;
              return (
                <div key={groupName} className="mb-5">
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">{groupName}</h3>
                  <div className="space-y-1">
                    {Object.entries(checks).map(([key, check]) => (
                      <div
                        key={key}
                        className={`flex items-center justify-between px-3 py-2 rounded-lg ${
                          check.status === 'fail' ? 'bg-red-50' : check.status === 'warn' ? 'bg-yellow-50' : 'bg-gray-50'
                        }`}
                        data-testid={`health-check-${key}`}
                      >
                        <div className="flex items-center gap-2">
                          <StatusIcon status={check.status} />
                          <span className="text-sm font-medium text-gray-700">{formatLabel(key)}</span>
                        </div>
                        <span className="text-xs text-gray-500">{check.detail}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!results && (
          <div className="bg-white rounded-xl shadow-sm border p-8 text-center mb-6">
            <Shield className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No health check results yet.</p>
            <p className="text-sm text-gray-400 mt-1">Click "Run Check" or wait for the next deploy to auto-run.</p>
          </div>
        )}

        {/* History */}
        {history.length > 1 && (
          <div className="bg-white rounded-xl shadow-sm border p-6" data-testid="health-history">
            <h2 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Clock className="h-4 w-4" /> Recent Check History
            </h2>
            <div className="space-y-2">
              {history.slice(0, 10).map((entry, i) => (
                <div key={i} className="flex items-center justify-between text-sm px-3 py-2 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    {entry.overall === 'healthy' && <CheckCircle2 className="h-4 w-4 text-green-500" />}
                    {entry.overall === 'unhealthy' && <XCircle className="h-4 w-4 text-red-500" />}
                    {entry.overall === 'degraded' && <AlertTriangle className="h-4 w-4 text-yellow-500" />}
                    <span className="font-medium text-gray-700">{(entry.overall || 'unknown').toUpperCase()}</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-500 text-xs">
                    <span>{entry.passed}/{entry.total_checks} passed</span>
                    <span>{entry.triggered_by === 'auto_startup' ? 'Auto' : 'Manual'}</span>
                    <span>{entry.timestamp ? new Date(entry.timestamp).toLocaleString() : ''}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="text-center text-xs text-gray-400 mt-6">
          Health checks run automatically after every deployment. Visit this page anytime to see the latest status.
        </p>
      </div>
    </div>
  );
}
